import React, { useEffect, useRef, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Sidebar from "../sidebar/sidebar";
import TopBar from "./TopBar";
import ApiEndpoint from "../../config/ApiEndpoint";
import ApiServices from "../../config/ApiServices";
import { apiFlagIsTrue } from "../../Utils/func";

const AppLayout = ({ children, props }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const profile = props?.profile || props?.profile?.data;
  const userType = profile?.user_type || profile?.data?.user_type;
  const token = profile?.token;
  const saveUserData = props?.save_user_data;
  const fetchedMasterOrgRef = useRef(false);
  const fetchedAdminRef = useRef(false);

  // Close sidebar when switching to desktop (e.g. resize)
  useEffect(() => {
    if (!isMobile) setSidebarOpen(false);
  }, [isMobile]);

  // Load master org profile + permission flags (account/get is source of truth for can_invite_clubs)
  useEffect(() => {
    if (userType !== "master_org" || !token || !saveUserData) return;

    const loadMasterOrgAccount = () => {
      const headers = { "Content-Type": "application/json", "x-access-token": token };
      return ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_ACCOUNT_GET,
        JSON.stringify({}),
        headers
      )
        .then((data) => {
          if (!data?.status || !data?.data) return;
          const d = data.data;
          const orgName = d.organisation_name ?? d.organisationDetails?.name ?? "";
          const logoUrl =
            d.profile_image ?? d.logo_url ?? d.organisationDetails?.logo_url ?? "";

          const updated = {
            ...profile,
            allow_member_emails: d.allow_member_emails ?? false,
            can_advertise: d.can_advertise ?? false,
            can_send_alerts: d.can_send_alerts ?? false,
            can_invite_clubs:
              d.can_invite_clubs == null
                ? true
                : apiFlagIsTrue(d.can_invite_clubs),
            show_community_join_requests: d.show_community_join_requests,
          };

          if (orgName) {
            updated.organisation_name = orgName;
            updated.organisationDetails = {
              ...(profile?.organisationDetails || {}),
              name: orgName,
              logo_url: logoUrl || profile?.organisationDetails?.logo_url,
            };
          }
          if (logoUrl) {
            updated.profile_image = logoUrl;
            updated.logo_url = logoUrl;
          }

          saveUserData({ user: updated });
        })
        .catch((err) => console.error("Master org account get failed", err));
    };

    if (!fetchedMasterOrgRef.current) {
      fetchedMasterOrgRef.current = true;
      loadMasterOrgAccount();
    }

    const onFocus = () => loadMasterOrgAccount();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [userType, token, saveUserData, profile]);

  // Load admin profile (organisation name + image) once so sidebar shows it on any admin page
  useEffect(() => {
    if (userType !== "admin" || !token || !saveUserData) return;
    if (fetchedAdminRef.current) return;
    fetchedAdminRef.current = true;

    const headers = { "Content-Type": "application/json", "x-access-token": token };
    ApiServices.PostApiCall(ApiEndpoint.ADMIN_PROFILE_GET, JSON.stringify({}), headers)
      .then((data) => {
        if (!data?.status || !data?.data) return;
        const d = data.data;
        const orgName = d.organisation_name ?? d.organisationDetails?.name ?? "";
        const imageUrl = d.profile_image ?? d.logo_url ?? "";
        if (!orgName && !imageUrl) return;
        const updated = {
          ...profile,
          organisation_name: orgName,
          profile_image: imageUrl || profile?.profile_image,
        };
        saveUserData({ user: updated });
      })
      .catch((err) => console.error("Admin profile get failed", err));
  }, [userType, token, saveUserData, profile]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", flexDirection: "column", maxWidth: "100%", overflowX: "hidden" }}>
      {/* Fixed TopBar */}
      <TopBar
        props={props}
        onMenuClick={isMobile ? () => setSidebarOpen(true) : undefined}
      />

      {/* Spacer to account for fixed TopBar */}
      <Box sx={{ height: { xs: "56px", md: "72px" } }} />

      {/* Main Content Area */}
      <Box sx={{ display: "flex", flex: 1, minWidth: 0, maxWidth: "100%" }}>
        <Sidebar
          profile={props?.profile}
          save_user_data={props?.save_user_data}
          {...props}
          mobileOpen={sidebarOpen}
          onMobileClose={() => setSidebarOpen(false)}
        />
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: "100%",
            overflowX: "hidden",
            marginLeft: { xs: 0, md: "280px" },
            backgroundColor: isDark ? "#121212" : "#f5f7fa",
            minHeight: { xs: "calc(100vh - 56px)", md: "calc(100vh - 72px)" },
          }}
        >
          <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, minWidth: 0, maxWidth: "100%" }}>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AppLayout;
